/**
 * Shopping List Page
 * Standalone checklist for organizing shopping items
 * 
 * STANDALONE: No budget/envelope integration - purely for list management
 * - Create lists, add items, mark as bought/unbought
 * - No price tracking or budget calculations
 * - Uses extended shopping catalogue for quick add
 * 
 * SAFETY GUARDS (Dec 2024):
 * - Idempotent add item (prevents duplicates)
 * - Write lock during submission
 * - Session-scoped deduplication
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Share2, ShoppingCart, ChevronDown, ChevronRight, Plus, X, ArrowLeft, Mic, MicOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import AutocompleteInput from '@/components/ui/AutocompleteInput';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { 
  getShoppingListById, 
  getListItems, 
  addItemToList, 
  deleteListItem, 
  updateListItem,
  toggleItemPurchased as toggleItemPurchasedService,
  clearPurchasedItems,
  clearAllItems,
  subscribeToListItems
} from '@/services/shoppingListService';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import AddTaskModal from '@/components/FinTasks/AddTaskModal';
import ShareButton from '@/components/ShareButton';
import ShareModal from '@/components/modals/ShareModal';
import { useTodoLists } from '@/hooks/useTodoLists';
import { SHOPPING_CATALOGUE, getCatalogueCategories, findCatalogueCategory, searchCatalogueItems } from '@/constants/shoppingCatalogue';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { getItemEmoji } from '@/utils/itemEmojis';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { playSuccessSound, playStartListeningSound, playStopListeningSound } from '@/utils/audioFeedback';
import { useSafeAction, createItemDedupeKey } from '@/hooks/useSafeAction';
import { FEATURE_FLAGS } from '@/config/featureFlags';

const ShoppingListPage = () => {
  const { currentUser } = useAuth();
  const { lists } = useTodoLists();
  const { listId } = useParams();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const navigate = useNavigate();
  
  // Safe action hook for idempotent writes
  const { execute: safeExecute, isExecuting: isSafeExecuting } = useSafeAction({
    debounceMs: 500, // 500ms debounce for add item
    enableDeduplication: true,
  });
  
  // Session-scoped deduplication tracking for items added in current session
  const addedItemsThisSessionRef = useRef(new Set());

  const [items, setItems] = useState([]);
  const [currentListName, setCurrentListName] = useState('Shopping List');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const [newItem, setNewItem] = useState({
    name: '',
    category: '',
    subcategory: '',
    quantity: 1,
    notes: ''
  });

  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [selectedCatalogueCategory, setSelectedCatalogueCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  const [listActionModal, setListActionModal] = useState({ open: false, action: null });
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedItemForTask, setSelectedItemForTask] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  // Per-item pending state to prevent double-taps and allow concurrent toggles
  const [pendingById, setPendingById] = useState({});

  // Catalogue: per-item quantity state and pending add state
  const [catalogueQtyById, setCatalogueQtyById] = useState({});
  const [pendingAddById, setPendingAddById] = useState({});

  // Ref for voice add function (to avoid stale closure in useVoiceInput)
  const voiceAddItemRef = useRef(null);

  // Voice input hook for hands-free item entry
  const { isListening, isSupported: voiceSupported, transcript, error: voiceError, toggleListening } = useVoiceInput({
    onResult: (text) => {
      // Auto-add the item using the ref function
      if (voiceAddItemRef.current) {
        voiceAddItemRef.current(text);
      }
    },
    onError: (error) => {
      toast.error(error);
    },
    language: 'en-GB'
  });

  // Wrapper for toggleListening with audio feedback
  const handleToggleListening = useCallback(() => {
    if (isListening) {
      playStopListeningSound();
    } else {
      playStartListeningSound();
    }
    toggleListening();
  }, [isListening, toggleListening]);

  const catalogueCategories = useMemo(() => getCatalogueCategories(), []);

  // Build a set of item names already in the shopping list (for "Added" indicator)
  const addedItemNamesSet = useMemo(() => {
    const set = new Set();
    (items || []).forEach((it) => {
      const key = (it?.name || '').toString().trim().toLowerCase();
      if (key) set.add(key);
    });
    return set;
  }, [items]);

  // Get the quantity in list for a given item name (for showing current qty)
  const getItemQtyInList = useCallback((itemName) => {
    const key = (itemName || '').toString().trim().toLowerCase();
    const found = items.find((it) => (it?.name || '').toString().trim().toLowerCase() === key);
    return found ? (Number(found.quantity) || 1) : 0;
  }, [items]);

  // Catalogue quantity helpers
  const getCatalogueQty = useCallback((itemKey) => {
    return Math.max(1, Number(catalogueQtyById[itemKey] || 1));
  }, [catalogueQtyById]);

  const incCatalogueQty = useCallback((itemKey) => {
    setCatalogueQtyById((prev) => ({ ...prev, [itemKey]: Math.min(99, getCatalogueQty(itemKey) + 1) }));
  }, [getCatalogueQty]);

  const decCatalogueQty = useCallback((itemKey) => {
    setCatalogueQtyById((prev) => ({ ...prev, [itemKey]: Math.max(1, getCatalogueQty(itemKey) - 1) }));
  }, [getCatalogueQty]);

  // FINLIST REAL-TIME: Subscribe to shopping list items for instant updates
  useEffect(() => {
    if (!currentUser?.uid || !listId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Subscribe to real-time updates
    const unsubscribe = subscribeToListItems(
      currentUser.uid,
      listId,
      ({ toBuy, purchased }) => {
        const allItems = [
          ...(toBuy || []),
          ...(purchased || [])
        ].map(item => ({
          ...item,
          purchased: item.purchased || item.status === 'purchased' || item.status === 'bought' || item.isBought || false,
          category: item.category || item.categoryId || '',
          subcategory: item.subcategory || '',
          createdAt: item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt || Date.now()),
          boughtAt: item.boughtAt instanceof Date ? item.boughtAt : (item.boughtAt ? new Date(item.boughtAt) : null)
        }));
        
        // Deduplicate: Firestore is the source of truth, replace local state
        // But ensure we don't have duplicate IDs from optimistic updates
        const seenIds = new Set();
        const deduplicatedItems = allItems.filter(item => {
          if (seenIds.has(item.id)) return false;
          seenIds.add(item.id);
          return true;
        });
        
        setItems(deduplicatedItems);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount or when dependencies change
    return () => unsubscribe();
  }, [currentUser?.uid, listId]);

  // Fetch list name (still one-time fetch, doesn't need real-time)
  useEffect(() => {
    const fetchListDetails = async () => {
      if (!currentUser?.uid || !listId) return;
      try {
        const list = await getShoppingListById(currentUser.uid, listId);
        if (list) setCurrentListName(list.name || 'Shopping List');
      } catch (error) {
        console.error('Error fetching list details:', error);
      }
    };
    fetchListDetails();
  }, [currentUser, listId]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.name.trim() || !currentUser?.uid || !listId) {
      toast.error('Please enter an item name');
      return;
    }
    
    // SAFETY: Create deduplication key for this exact item
    const itemNameNormalized = newItem.name.trim().toLowerCase();
    const dedupeKey = createItemDedupeKey('add-item', itemNameNormalized, listId);
    
    // Guard: Check if already adding (prevents rapid clicks)
    if (adding || isSafeExecuting) {
      if (import.meta.env.DEV) {
        console.log('[AddItem] Blocked - already adding');
      }
      return;
    }
    
    // Execute with safe action wrapper
    const result = await safeExecute('add-item-form', async () => {
      setAdding(true);
      
      try {
        // Auto-detect category from catalogue if not already set
        let category = newItem.category;
        let subcategory = newItem.subcategory;
        
        if (!category) {
          // Search the catalogue for a matching item
          const catalogueMatches = searchCatalogueItems(newItem.name.trim());
          if (catalogueMatches.length > 0) {
            // Use the first (best) match
            const match = catalogueMatches[0];
            category = match.category;
            subcategory = match.subcategory;
          } else {
            // Default to "Other" if no match found
            category = 'Other';
            subcategory = '';
          }
        }

        const itemData = {
          name: newItem.name.trim(),
          category: category,
          subcategory: subcategory,
          quantity: parseInt(newItem.quantity) || 1,
          notes: newItem.notes.trim()
        };
        
        // Use direct Firestore service (no backend dependency)
        const addedItem = await addItemToList(currentUser.uid, listId, itemData);
        
        if (addedItem) {
          // Track this item in session for reference
          addedItemsThisSessionRef.current.add(addedItem.id);
          
          // Clear input form - Firestore subscription will add item to list
          setNewItem({ name: '', category: '', subcategory: '', quantity: 1, notes: '' });
        }
        
        return addedItem;
      } finally {
        setAdding(false);
      }
    }, { dedupeKey });
    
    // Handle skipped execution (don't show error, it's intentional)
    if (result.skipped) {
      if (import.meta.env.DEV) {
        console.log('[AddItem] Skipped:', result.reason);
      }
      return;
    }
    
    // Handle actual errors
    if (!result.success && result.error) {
      console.error('Error adding item:', result.error);
      toast.error(result.error.message || 'Failed to add item');
    }
  };

  const handleQuickAddItem = async (itemName, category, subcategory, qtyToAdd = 1) => {
    if (!currentUser?.uid || !listId) return;
    
    const itemKey = itemName.toLowerCase().trim();
    
    // Skip if already pending
    if (pendingAddById[itemKey]) {
      if (import.meta?.env?.DEV) console.log('[Catalogue] Skipping - already pending:', itemName);
      return;
    }
    
    // Mark as pending
    setPendingAddById(prev => ({ ...prev, [itemKey]: true }));
    
    try {
      // Check if item already exists in list
      const existingItem = items.find(it => 
        (it?.name || '').toString().trim().toLowerCase() === itemKey
      );
      
      if (existingItem) {
        // Increment quantity of existing item
        const currentQty = Number(existingItem.quantity) || 1;
        const newQty = currentQty + Number(qtyToAdd);
        
        await updateListItem(currentUser.uid, listId, existingItem.id, { quantity: newQty });
        
        // Firestore subscription will update the item quantity
        if (import.meta?.env?.DEV) console.log('[Catalogue] Incremented qty:', itemName, currentQty, '→', newQty);
        // Visual feedback via catalogue highlight - no toast needed
      } else {
        // Add new item
        const itemData = { 
          name: itemName, 
          category: category || '', 
          subcategory: subcategory || '', 
          quantity: Number(qtyToAdd) || 1, 
          notes: '' 
        };
        
        const addedItem = await addItemToList(currentUser.uid, listId, itemData);
        if (addedItem) {
          // Track this item in session for reference
          addedItemsThisSessionRef.current.add(addedItem.id);
          
          // Firestore subscription will add the item to list
          if (import.meta?.env?.DEV) console.log('[Catalogue] Added new:', itemName, 'qty:', qtyToAdd);
        }
      }
      
      // Reset quantity for this item in catalogue
      setCatalogueQtyById(prev => ({ ...prev, [itemKey]: 1 }));
      
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    } finally {
      setPendingAddById(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  // Voice add item - automatically adds item with category detection
  const handleVoiceAddItem = useCallback(async (spokenText) => {
    if (!spokenText?.trim() || !currentUser?.uid || !listId) {
      toast.error('Could not add item');
      return;
    }

    const itemName = spokenText.trim();
    
    // Auto-detect category from catalogue
    let category = '';
    let subcategory = '';
    const catalogueMatches = searchCatalogueItems(itemName);
    if (catalogueMatches.length > 0) {
      const match = catalogueMatches[0];
      category = match.category;
      subcategory = match.subcategory;
    } else {
      category = 'Other';
    }

    try {
      const itemData = { 
        name: itemName, 
        category, 
        subcategory, 
        quantity: 1, 
        notes: '' 
      };
      
      const addedItem = await addItemToList(currentUser.uid, listId, itemData);
      if (addedItem) {
        // Track this item in session for reference
        addedItemsThisSessionRef.current.add(addedItem.id);
        
        // Firestore subscription will add the item to list
        // Play success sound for voice feedback
        playSuccessSound();
      }
    } catch (error) {
      console.error('Voice add error:', error);
      toast.error('Failed to add item');
    }
  }, [currentUser?.uid, listId]);

  // Update the ref so voice input can use it
  useEffect(() => {
    voiceAddItemRef.current = handleVoiceAddItem;
  }, [handleVoiceAddItem]);

  const handleTogglePurchased = async (itemId) => {
    if (!currentUser?.uid || !listId) return;
    
    // Skip if this item already has a pending toggle
    if (pendingById[itemId]) {
      if (import.meta?.env?.DEV) console.log('[ShoppingList] Skipping toggle - already pending:', itemId);
      return;
    }
    
    // Mark this item as pending
    setPendingById(prev => ({ ...prev, [itemId]: true }));
    
    // Optimistic UI update using functional state (avoids stale closure)
    // Capture the toggled state for later use
    let newPurchasedState = null;
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        newPurchasedState = !item.purchased;
        return { ...item, purchased: newPurchasedState };
      }
      return item;
    }));
    
    try {
      // Service reads fresh state from Firestore and toggles atomically
      // We don't pass markAsBought - let the service determine state from server
      await toggleItemPurchasedService(currentUser.uid, listId, itemId);
      
      // Update local state with boughtAt timestamp
      setItems(prev => prev.map(item => {
        if (item.id === itemId) {
          // Use the toggled state we captured
          const isPurchased = newPurchasedState !== null ? newPurchasedState : !item.purchased;
          return { 
            ...item, 
            purchased: isPurchased,
            boughtAt: isPurchased ? new Date() : null
          };
        }
        return item;
      }));
      
      if (import.meta?.env?.DEV) console.log('[ShoppingList] Toggle success:', itemId, '→', newPurchasedState);
      toast.success(newPurchasedState ? 'Item marked as bought ✓' : 'Item moved back to list');
    } catch (error) {
      console.error('Error updating item:', error);
      
      // Rollback only this specific item (not the entire list)
      setItems(prev => prev.map(item => {
        if (item.id === itemId) {
          // Reverse the optimistic update
          return { ...item, purchased: !newPurchasedState };
        }
        return item;
      }));
      
      toast.error(error.message || 'Failed to update item');
    } finally {
      // Clear pending state for this item
      setPendingById(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!currentUser?.uid || !listId) return;
    
    const previousItems = [...items];
    setItems(prev => prev.filter(item => item.id !== itemId));
    try {
      // Use direct Firestore service (no backend dependency)
      await deleteListItem(currentUser.uid, listId, itemId);
      toast.success('Item removed from list');
    } catch (error) {
      setItems(previousItems);
      console.error('Error deleting item:', error);
      toast.error(error.message || 'Failed to delete item');
    }
  };

  const handleClearList = (action) => setListActionModal({ open: true, action });

  const confirmClearList = async () => {
    if (!currentUser?.uid || !listId) return;
    
    const { action } = listActionModal;
    try {
      if (action === 'clearPurchased') {
        // Use direct Firestore service (no backend dependency)
        const response = await clearPurchasedItems(currentUser.uid, listId);
        if (response.success) {
          setItems(prev => prev.filter(item => !item.purchased));
          toast.success((response.data.deletedCount || 0) + ' bought items cleared');
        }
      } else if (action === 'clearAll') {
        // Use direct Firestore service (no backend dependency)
        const response = await clearAllItems(currentUser.uid, listId);
        if (response.success) {
          setItems([]);
          toast.success('All items cleared - starting fresh!');
        }
      }
    } catch (error) {
      console.error('Error clearing items:', error);
      toast.error(error.message || 'Failed to clear items');
    } finally {
      setListActionModal({ open: false, action: null });
    }
  };

  const handleCreateTaskFromItem = (item) => {
    setSelectedItemForTask(item);
    setShowTaskModal(true);
  };

  const handleAddTaskFromItem = async () => {
    toast.success('Reminder created!');
    setShowTaskModal(false);
    setSelectedItemForTask(null);
  };

  // Open share modal
  const handleShareList = () => {
    setShowShareModal(true);
  };

  const unpurchasedItems = items.filter(item => !item.purchased);
  const purchasedItems = items.filter(item => item.purchased);
  const totalItems = items.length;
  const completedItems = purchasedItems.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" text="Loading your shopping list..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-20">
      <main className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          {/* Back Navigation */}
          <button 
            onClick={() => navigate('/shopping-lists')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Shopping Lists</span>
          </button>
          
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{currentListName}</h1>
            <div className="flex items-center gap-2">
              {/* Share Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                onClick={handleShareList}
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
              {/* Options Dropdown */}
              <div className="relative group">
                <Button variant="outline" size="sm" className="gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                Options
              </Button>
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-1">
                  <Link to="/tasks" className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                    <Bell className="h-4 w-4 text-teal-600" />
                    <div><div className="font-medium">View Tasks</div></div>
                  </Link>
                  {purchasedItems.length > 0 && (
                    <button onClick={() => handleClearList('clearPurchased')} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                      <div><div className="font-medium">Clear Bought</div><div className="text-xs text-gray-500 dark:text-gray-400">Remove {purchasedItems.length} items</div></div>
                    </button>
                  )}
                  {totalItems > 0 && (
                    <button onClick={() => handleClearList('clearAll')} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                      <div><div className="font-medium">Start New List</div><div className="text-xs text-gray-500 dark:text-gray-400">Clear all {totalItems} items</div></div>
                    </button>
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-900 dark:text-gray-100">{totalItems} items</span>
            <span>•</span>
            <span className="text-green-600 dark:text-green-400">{completedItems} bought</span>
            <span>•</span>
            <span className="text-orange-600 dark:text-orange-400">{unpurchasedItems.length} remaining</span>
          </div>
          <div className="mt-4">
            <Link to="/tasks" className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-50 hover:bg-teal-100 dark:bg-teal-900/30 dark:hover:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded-xl text-sm font-medium transition-colors border border-teal-200 dark:border-teal-800">
              <Bell className="h-4 w-4" />View Tasks
            </Link>
          </div>
        </div>

        <Card className="mb-6 border-green-200 bg-gradient-to-br from-green-50 via-white to-white dark:from-green-900/20 dark:via-gray-800 dark:to-gray-800 dark:border-green-800">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-green-900 dark:text-green-300"><ShoppingCart className="h-5 w-5" />Quick Add</span>
              <Button variant="outline" size="sm" onClick={() => setShowQuickAdd(!showQuickAdd)} className="text-green-700 border-green-300 hover:bg-green-100 dark:text-green-300 dark:border-green-700">
                {showQuickAdd ? 'Hide Catalogue' : 'Browse Catalogue'}
                {showQuickAdd ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronRight className="ml-1 h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          {showQuickAdd && (
            <CardContent>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {catalogueCategories.map((cat) => (
                    <button key={cat.id} onClick={() => { setSelectedCatalogueCategory(selectedCatalogueCategory?.id === cat.id ? null : cat); setSelectedSubcategory(null); }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCatalogueCategory?.id === cat.id ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                      {cat.emoji} {cat.name}
                    </button>
                  ))}
                </div>
                {selectedCatalogueCategory?.subcategories && (
                  <div className="border-t pt-4 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select subcategory:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCatalogueCategory.subcategories.map((sub) => (
                        <button key={sub.id} onClick={() => setSelectedSubcategory(selectedSubcategory?.id === sub.id ? null : sub)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${selectedSubcategory?.id === sub.id ? 'bg-green-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                          {sub.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {selectedSubcategory?.items && (
                  <div className="border-t pt-4 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tap to add:</p>
                    <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                      {selectedSubcategory.items.map((itemName, idx) => {
                        const itemKey = itemName.toLowerCase().trim();
                        const isAdded = addedItemNamesSet.has(itemKey);
                        const qtyInList = getItemQtyInList(itemName);
                        const selectedQty = getCatalogueQty(itemKey);
                        const isPending = !!pendingAddById[itemKey];
                        
                        return (
                          <div key={idx} className={`flex items-center gap-1 rounded-lg text-sm border transition-all ${isAdded ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700' : 'bg-white dark:bg-gray-600 border-gray-200 dark:border-gray-500'}`}>
                            {/* Item name with Added indicator */}
                            <div className="flex items-center gap-1 px-2 py-1.5">
                              {isAdded ? (
                                <span className="text-green-600 dark:text-green-400 text-xs font-medium">✓ {qtyInList}</span>
                              ) : (
                                <Plus className="h-3 w-3 text-gray-400" />
                              )}
                              <span className={isAdded ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-200'}>{itemName}</span>
                            </div>
                            
                            {/* Quantity controls */}
                            <div className="flex items-center border-l border-gray-200 dark:border-gray-500">
                              <button 
                                onClick={(e) => { e.stopPropagation(); decCatalogueQty(itemKey); }}
                                disabled={isPending || selectedQty <= 1}
                                className="px-1.5 py-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30"
                              >
                                −
                              </button>
                              <span className="w-5 text-center text-xs font-medium text-gray-700 dark:text-gray-200">{selectedQty}</span>
                              <button 
                                onClick={(e) => { e.stopPropagation(); incCatalogueQty(itemKey); }}
                                disabled={isPending || selectedQty >= 99}
                                className="px-1.5 py-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30"
                              >
                                +
                              </button>
                            </div>
                            
                            {/* Add button */}
                            <button 
                              onClick={() => handleQuickAddItem(itemName, selectedCatalogueCategory.name, selectedSubcategory.name, selectedQty)}
                              disabled={isPending}
                              className={`px-2 py-1.5 rounded-r-lg font-medium text-xs transition-colors ${isPending ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-wait' : isAdded ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 text-green-700 dark:text-green-200'}`}
                            >
                              {isPending ? '...' : isAdded ? '+' : 'Add'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        <Card className="mb-6 border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white dark:from-blue-900/20 dark:via-gray-800 dark:to-gray-800 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900 dark:text-blue-300">
              <Plus className="h-5 w-5" />
              Add Item Manually
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddItem}>
              <div className="space-y-4">
                {/* Item Name - Main Input with Voice Button */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Item Name <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <AutocompleteInput 
                      name="name" 
                      placeholder={isListening ? "Listening..." : "Type item name (e.g., Bananas, Milk, Bread...)"} 
                      value={newItem.name}
                      onChange={(e) => { 
                        const words = e.target.value.split(' '); 
                        const capitalized = words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' '); 
                        setNewItem(prev => ({ ...prev, name: capitalized })); 
                      }}
                      onSelect={(suggestion) => { 
                        setNewItem(prev => ({ 
                          ...prev, 
                          name: suggestion.name, 
                          category: suggestion.category || prev.category, 
                          quantity: suggestion.quantity || prev.quantity, 
                          notes: suggestion.notes || prev.notes 
                        })); 
                      }}
                      disabled={adding || isListening}
                      className="flex-1"
                    />
                    {voiceSupported && (
                      <Button
                        type="button"
                        onClick={handleToggleListening}
                        variant={isListening ? "destructive" : "outline"}
                        className={`p-3 ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                        title={isListening ? "Stop listening" : "Use voice to add item"}
                      >
                        {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                      </Button>
                    )}
                  </div>
                  {isListening && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1 animate-pulse">
                      🎤 Listening... Speak the item name
                    </p>
                  )}
                </div>
                
                {/* Quantity and Notes Row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quantity
                    </label>
                    <Input 
                      name="quantity" 
                      type="number" 
                      min="1" 
                      placeholder="1" 
                      value={newItem.quantity} 
                      onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))} 
                      disabled={adding} 
                      className="w-full"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Notes <span className="text-gray-400 text-xs">(optional)</span>
                    </label>
                    <Input 
                      name="notes" 
                      placeholder="e.g., brand preference, size..." 
                      value={newItem.notes} 
                      onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))} 
                      disabled={adding}
                      className="w-full"
                    />
                  </div>
                </div>
                
                {/* Submit Button */}
                <div className="flex justify-end">
                  <Button 
                    type="submit" 
                    disabled={adding || isSafeExecuting || !newItem.name.trim()} 
                    loading={adding || isSafeExecuting} 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to List
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {unpurchasedItems.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><span>To Buy</span><span className="text-sm font-normal text-gray-500 dark:text-gray-400">({unpurchasedItems.length} items)</span></CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <AnimatePresence mode="popLayout">
                    {unpurchasedItems.map((item, index) => (
                      <motion.div key={item.id} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -100 }} transition={{ duration: 0.2, delay: index * 0.03 }}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                        <div className="flex items-center space-x-3">
                          <button onClick={() => handleTogglePurchased(item.id)} disabled={!!pendingById[item.id]} className={`w-6 h-6 rounded-full border-2 transition-colors flex items-center justify-center ${pendingById[item.id] ? 'border-gray-200 dark:border-gray-600 cursor-wait opacity-50' : 'border-gray-300 dark:border-gray-500 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/30'}`} aria-label="Mark as bought" />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{getItemEmoji(item.name, item.category)}</span>
                              <span className="font-medium text-gray-900 dark:text-white">{item.name}</span>
                              {item.quantity > 1 && <span className="text-sm text-gray-500 dark:text-gray-400">(x{item.quantity})</span>}
                              {item.category && <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded text-gray-600 dark:text-gray-300">{item.category}</span>}
                            </div>
                            {item.notes && <p className="text-sm text-gray-500 dark:text-gray-400">{item.notes}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleCreateTaskFromItem(item)} className="text-purple-600 hover:text-purple-700 dark:text-purple-400 p-1" title="Create reminder"><Bell className="w-4 h-4" /></button>
                          <button onClick={() => handleDeleteItem(item.id)} className="text-red-500 hover:text-red-700 p-1" title="Remove item"><X className="w-4 h-4" /></button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </CardContent>
            </Card>
          )}

          {purchasedItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2"><span>Bought</span><span className="text-sm font-normal text-gray-500 dark:text-gray-400">({purchasedItems.length} items)</span></span>
                  <Button variant="secondary" size="sm" onClick={() => handleClearList('clearPurchased')}>Clear Bought</Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <AnimatePresence mode="popLayout">
                    {purchasedItems.map((item, index) => (
                      <motion.div key={item.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 0.7, scale: 1 }} exit={{ opacity: 0, x: 100 }} transition={{ duration: 0.2, delay: index * 0.03 }}
                        className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <button onClick={() => handleTogglePurchased(item.id)} disabled={!!pendingById[item.id]} className={`w-6 h-6 rounded-full flex items-center justify-center ${pendingById[item.id] ? 'bg-green-300 cursor-wait opacity-50' : 'bg-green-500 text-white'}`} aria-label="Mark as not bought">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          </button>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xl opacity-50">{getItemEmoji(item.name, item.category)}</span>
                              <span className="font-medium line-through text-gray-500 dark:text-gray-400">{item.name}</span>
                              {item.quantity > 1 && <span className="text-sm text-gray-400">(x{item.quantity})</span>}
                            </div>
                            {item.notes && <p className="text-sm text-gray-400 line-through">{item.notes}</p>}
                          </div>
                        </div>
                        <button onClick={() => handleCreateTaskFromItem(item)} className="text-purple-500 hover:text-purple-600 p-1" title="Create reminder"><Bell className="w-4 h-4" /></button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </CardContent>
            </Card>
          )}

          {items.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <ShoppingCart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Your shopping list is empty</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Add items using the form above or browse the catalogue for quick add.</p>
                <Button onClick={() => setShowQuickAdd(true)} className="bg-green-600 hover:bg-green-700"><ShoppingCart className="h-4 w-4 mr-2" />Browse Catalogue</Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      
      <Modal open={listActionModal.open} title={listActionModal.action === 'clearAll' ? 'Start New List?' : 'Clear Bought Items?'} onClose={() => setListActionModal({ open: false, action: null })} onConfirm={confirmClearList} confirmText="Confirm" variant={listActionModal.action === 'clearAll' ? 'danger' : 'primary'}>
        <div className="space-y-4">
          {listActionModal.action === 'clearAll' ? (
            <div className="flex items-start gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600 dark:text-purple-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
              <div><h4 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-1">This will clear ALL items</h4><p className="text-sm text-purple-800 dark:text-purple-200">All {totalItems} items will be removed.</p></div>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
              <div><h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">Clear {purchasedItems.length} bought items</h4><p className="text-sm text-blue-800 dark:text-blue-200">Your {unpurchasedItems.length} remaining items will stay.</p></div>
            </div>
          )}
        </div>
      </Modal>

      {selectedItemForTask && (
        <AddTaskModal isOpen={showTaskModal} onClose={() => { setShowTaskModal(false); setSelectedItemForTask(null); }} onSubmit={handleAddTaskFromItem} prefillListId={lists.find(l => l.context === 'shopping')?.id}
          prefillData={{ title: 'Buy ' + selectedItemForTask.name, description: selectedItemForTask.notes || 'Purchase ' + selectedItemForTask.quantity + 'x ' + selectedItemForTask.name, linkedType: 'shopping-item', linkedRef: selectedItemForTask.id, tags: [selectedItemForTask.category || 'shopping'] }} />
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        listId={listId}
        listName={currentListName}
        items={items}
      />

      <ConfirmDialog />
    </div>
  );
};

export default ShoppingListPage;
